import { IRowNode } from 'ag-grid-community';

import { Component, Input, OnInit } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BudgetAggregate } from '@core/models/budget-aggregate.model';
import { Project } from '@core/models/project.model';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BaseComponent } from '@portal-shared/app/components/base-components/base.component';
import { Classification } from '@portal-shared/app/entities/class.model';
import { Department } from '@portal-shared/app/entities/department.model';
import { FormatNumberPipe } from '@portal-shared/app/pipes/format-number.pipe';
import { FormatUtils } from '@portal-shared/app/utils/format.utils';
import { PlannableCardComponent } from '@resource-management/task-board/cell-renderers/plannable-card/plannable-card.component';
import { ClassGroupRow } from '@shared/components/grids/rows/groups/class-group.row';
import { DepartmentGroupRow } from '@shared/components/grids/rows/groups/department-group.row';
import { ProjectGroupRow } from '@shared/components/grids/rows/groups/project-group.row';
import { Subject, Subscription } from 'rxjs';
import { PlanningBoardGroupRowType } from '../grids/rows/groups/planning-board-group-row-type.enum';

export type RowBudgetGroupingEntity = Project | Department | Classification;
export type BudgetedGroupingRow = ProjectGroupRow | DepartmentGroupRow | ClassGroupRow;

@Component({
  selector: 'al-budget-group-base',
  templateUrl: './budget-group-base.component.html',
  styleUrls: ['./budget-group-base.component.scss'],
  imports: [FontAwesomeModule, MatTooltipModule, FormatNumberPipe, TranslateModule]
})
export class BudgetGroupBaseComponent extends BaseComponent implements OnInit {
  @Input() public translationKey = '';
  @Input() public node?: IRowNode<BudgetedGroupingRow>;

  @Input() public icon?: IconDefinition;
  @Input() public refreshSubject?: Subject<void>;
  @Input() public allowExpandAll?: boolean;

  public budget?: BudgetAggregate;

  public tooltip = '';
  public remainingHoursTooltip = '';
  private formatBudgetTooltipSubscription?: Subscription;

  public get hoursToPlan() {
    if (!this.budget) return 0;
    const { budgetedHours, actualHours, plannedHours } = this.budget;
    return budgetedHours - actualHours - plannedHours;
  }

  public constructor(private translateService: TranslateService) {
    super();
  }

  public ngOnInit() {
    if (this.translationKey) {
      this.subscribe(
        this.translateService.get(this.translationKey).subscribe((translation: string) => {
          this.tooltip = `${translation}\n ${this.node?.data?.label ?? ''}`;
        })
      );
    }

    if (this.node?.data?.type === PlanningBoardGroupRowType.Project) {
      this.setProjectTooltip();
    }

    if (this.refreshSubject) {
      this.subscribe(
        this.refreshSubject.subscribe(() => {
          this.refresh();
        })
      );
    }

    this.refresh();
  }

  public setProjectTooltip(): void {
    if (!this.node) return;

    const projectGroupRow = this.node.data as ProjectGroupRow;

    const customerInfo = FormatUtils.formatTitle({
      code: projectGroupRow.project.customerCode,
      description: projectGroupRow.project.customerDesc
    });

    this.subscribe(
      this.translateService.stream('common.customer').subscribe((customerLabel: string) => {
        const customerTooltip = this.formatEntityTooltip(customerLabel, customerInfo);
        this.tooltip = this.tooltip ? `${this.tooltip}\n\n${customerTooltip}` : customerTooltip;
      })
    );
  }

  public refresh() {
    this.budget = this.node?.data?.dto?.budgetAggregate;
    this.setBudgetTooltip();
  }

  public expand(expand = true) {
    if (!this.node?.group) return;

    this.node?.setExpanded(expand);
  }
  public toggleExpand() {
    this.expand(!this.node?.expanded);
  }

  public toggleExpandAll() {
    if (!this.node?.group) return;

    if (this.node.data) {
      this.node.data.expandAll = true;
    }
    this.node.setExpanded(true);
  }

  public setBudgetTooltip() {
    if (!this.budget) return;

    if (this.formatBudgetTooltipSubscription) {
      this.formatBudgetTooltipSubscription.unsubscribe();
    }

    this.formatBudgetTooltipSubscription = PlannableCardComponent.formatBudgetTooltip(
      this.budget,
      this.translateService
    ).subscribe(tooltip => {
      this.remainingHoursTooltip = tooltip;
    });

    this.subscribe(this.formatBudgetTooltipSubscription);
  }

  private formatEntityTooltip(title: string, entityLabel?: string) {
    let tooltip = title;
    if (entityLabel) {
      tooltip += '\n' + entityLabel;
    }
    return tooltip;
  }
}
